import { usePaymentTypes } from '@/features/proposals/hooks/use-payment-types.hook';
import type { PaymentType } from '@/services';

export const CAPITAL_GLOBAL_PAYMENT_PRODUCT_TYPE = 'CAPITAL_GLOBAL' as const;

export const LEGACY_GENERIC_DEBIT_PAYMENT_CODE = '1000013';
export const LEGACY_BOLETO_PAYMENT_CODE = '1000014';

const LEGACY_PAYMENT_METHOD_LABELS: Record<string, string> = {
  [LEGACY_GENERIC_DEBIT_PAYMENT_CODE]: 'Débito em Conta',
  [LEGACY_BOLETO_PAYMENT_CODE]: 'Boleto',
};

export function isDebitPaymentDescription(description: string): boolean {
  return /débito|debito/i.test(description);
}

export function isBoletoPaymentDescription(description: string): boolean {
  return /boleto/i.test(description);
}

export function filterCapitalGlobalPaymentTypes(
  paymentTypes: PaymentType[]
): PaymentType[] {
  return paymentTypes.filter(
    paymentType =>
      isDebitPaymentDescription(paymentType.description) ||
      isBoletoPaymentDescription(paymentType.description)
  );
}

export function getPaymentTypeByCode(
  code: string,
  paymentTypes: PaymentType[]
): PaymentType | undefined {
  const trimmed = String(code || '').trim();
  if (!trimmed) return undefined;
  return paymentTypes.find(paymentType => paymentType.code === trimmed);
}

export function isDebitPaymentType(paymentType?: PaymentType): boolean {
  if (!paymentType) return false;
  return isDebitPaymentDescription(paymentType.description);
}

export function requiresBankAccountForPayment(
  paymentMethodCode: string,
  paymentTypes: PaymentType[]
): boolean {
  const paymentType = getPaymentTypeByCode(paymentMethodCode, paymentTypes);
  if (paymentType) return isDebitPaymentType(paymentType);
  return paymentMethodCode === LEGACY_GENERIC_DEBIT_PAYMENT_CODE;
}

export function resolveCapitalGlobalPaymentMethodLabel(
  code: string,
  paymentTypes?: PaymentType[]
): string {
  const trimmed = String(code || '').trim();
  if (!trimmed) return '—';

  const fromCatalog = paymentTypes
    ? getPaymentTypeByCode(trimmed, paymentTypes)
    : undefined;
  if (fromCatalog) return fromCatalog.description;

  return LEGACY_PAYMENT_METHOD_LABELS[trimmed] ?? trimmed;
}

export function useCapitalGlobalProposalPaymentTypes() {
  const query = usePaymentTypes(CAPITAL_GLOBAL_PAYMENT_PRODUCT_TYPE);
  const paymentTypes = filterCapitalGlobalPaymentTypes(query.data ?? []);

  const paymentMethodOptions = paymentTypes.map(paymentType => ({
    value: paymentType.code,
    label: paymentType.description,
  }));

  return {
    ...query,
    paymentTypes,
    paymentMethodOptions,
    isCatalogReady: !query.isLoading && !query.isError,
    isCatalogEmpty: !query.isLoading && !query.isError && paymentTypes.length === 0,
  };
}
